import { TranscriptTrack } from '@src/editor/models/TranscriptTrack';

import type { Timeline } from '@src/editor/models/Timeline';

/** Only the fields the lookup reads — `getFullTranscript()`'s token shape. */
type TranscriptTokenLike = {
  type?: string;
  state?: string;
  clip_segment_id?: string;
};

const isActiveWord = (token: TranscriptTokenLike) =>
  token.type === 'text' && token.state !== 'removed' && token.state !== 'cut';

/**
 * PLAT-11156 — the first hop, for every Section at once: each Section's
 * timeline element ids resolved to the `clipTranscriptSegment.id`s its tokens
 * carry. One set per input, in input order; an id that is not on the transcript
 * track contributes nothing. Taking all the Sections together keeps the track
 * walk to one pass instead of one per Section.
 */
export function reelSectionSegmentIdSets(
  timeline: Timeline,
  sectionElementIds: readonly (readonly string[])[],
): ReadonlySet<string>[] {
  const track = timeline.tracks.find(
    (t): t is TranscriptTrack => t instanceof TranscriptTrack,
  );
  const elementsById = track
    ? new Map(track.elements.map(el => [el.id, el]))
    : undefined;

  return sectionElementIds.map(elementIds => {
    const segmentIds = new Set<string>();
    for (const id of elementIds) {
      const segmentId = elementsById?.get(id)?.getClipTranscriptSegment().id;
      if (segmentId) segmentIds.add(segmentId);
    }
    return segmentIds;
  });
}

/**
 * PLAT-11061 — where a Section's words start in the transcript's own array.
 *
 * The Reel Editor holds **timeline element ids** (`ReelSection.elementIds`),
 * while every jump the transcript offers takes an **index into
 * `getFullTranscript()`**. The two id spaces look interchangeable and are not,
 * so getting from one to the other is two hops:
 *
 *   1. element id → that element on the transcript track → its
 *      `clipTranscriptSegment.id`, which is what the tokens carry;
 *   2. the first token whose `clip_segment_id` is one of those.
 *
 * Every one of the Section's elements is hopped, not just the first: a Section
 * made of several elements whose opening words have all been deleted still has
 * words further in, and those are what the creator asked to be shown.
 *
 * Returns `undefined` rather than 0 when nothing resolves — index 0 is the very
 * top of the transcript, so a failed lookup that returned it would silently
 * scroll to the wrong passage instead of doing nothing.
 */
export function reelSectionTranscriptIndex(
  timeline: Timeline,
  transcriptElements: readonly TranscriptTokenLike[] | undefined,
  elementIds: readonly string[],
): number | undefined {
  if (!transcriptElements?.length || elementIds.length === 0) return undefined;

  const [segmentIds] = reelSectionSegmentIdSets(timeline, [elementIds]);
  if (segmentIds.size === 0) return undefined;

  // One pass in reading order, so the first match is the Section's opening
  // word. An active *text* token specifically: a removed word left struck by a
  // montage move carries the same segment id as its kept copy, and punctuation
  // carries no timing to seek to.
  const index = transcriptElements.findIndex(
    token =>
      token.clip_segment_id !== undefined &&
      segmentIds.has(token.clip_segment_id) &&
      isActiveWord(token),
  );

  return index === -1 ? undefined : index;
}
